import { faAmazon, faDiscord, faSnapchat, faTelegram, faTwitch, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ReorderIcon from '@mui/icons-material/Reorder';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    alpha,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem as DropdownMenuItem,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    Link as MuiLink,
    MenuItem as MuiMenuItem,
    Select,
    Skeleton,
    Stack,
    SvgIcon,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useDialog } from 'muibox';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import BackNavigationButton from '../../components/common/BackNavigationButton';
import LargeMaterialUISwitch from '../../components/common/LargeMaterialUISwitch';
import PageContainer from '../../components/common/PageContainter';
import StyledCard from '../../components/common/StyledCard';
import StyledCardTitleBar from '../../components/common/StyledCardTitleBar';
import StyledDialogActions from '../../components/common/StyledDialogActions';
import IconInstagram from '../../components/icons/IconInstagram';
import IconOnlyFans from '../../components/icons/IconOnlyFans';
import IconReddit from '../../components/icons/IconReddit';
import IconTikTok from '../../components/icons/IconTikTok';
import IconTwitter from '../../components/icons/IconTwitter';
import useOnlyPromoteLinks, { OnlyPromoteLink } from '../../hooks/useOnlyPromoteLinks';
import useOnlyPromoteReferrers, { OnlyPromoteReferrer } from '../../hooks/useOnlyPromoteReferrers';
import useCampaignsAndTrialsTrackingMetrics from '../../hooks/useSextforceCampaignsAndTrialsTrackingMetrics';
import useSextforceMetricsCampaignsCreate from '../../hooks/useSextforceMetricsCampaignsCreate';
import { OnlyFansMetrics } from '../../hooks/useSextforceMetricsCampaignsOverview';
import useSextforceMetricsTrialsCreate from '../../hooks/useSextforceMetricsTrialsCreate';
import useSubscriber, { OnlyFansSextforcePpvMenuPublicProfile } from '../../hooks/useSubscriber';
import useSubscriberMe from '../../hooks/useSubscriberMe';
import { SettingsContext } from '../../store/SettingsContext';
import { UserContext } from '../../store/UserContext';
import { buildMetricTrackingUrl, findMetricIdForSavedTrackingLink } from '../../utils/metricTrackingLink';
import {
    normalizeOnlyPromoteCountryCode,
    ONLY_PROMOTE_COUNTRY_OPTIONS,
    onlyPromoteCountryName,
} from '../../utils/onlyPromoteCountryOptions';
import LinkReorderModal from './components/LinkReorderModal';
import OnlyPromoteStatsCard from './components/OnlyPromoteStatsCard';

const PUBLIC_PROFILE_NAME_MAX = 60;
const PUBLIC_PROFILE_DESCRIPTION_MAX = 250;

const LINK_PLATFORMS = [
    'onlyfans',
    'fansly',
    'instagram',
    'twitter',
    'tiktok',
    'snapchat',
    'youtube',
    'twitch',
    'reddit',
    'telegram',
    'discord',
    'linktree',
    'amazon',
    'cashapp',
    'other',
] as const;

const PLATFORM_LABELS: Record<string, string> = {
    onlyfans: 'OnlyFans',
    fansly: 'Fansly',
    instagram: 'Instagram',
    twitter: 'X / Twitter',
    tiktok: 'TikTok',
    snapchat: 'Snapchat',
    youtube: 'YouTube',
    twitch: 'Twitch',
    reddit: 'Reddit',
    telegram: 'Telegram',
    discord: 'Discord',
    linktree: 'Linktree',
    amazon: 'Amazon',
    cashapp: 'Cash App',
    other: 'Other',
};

const platformLabel = (key: string) => PLATFORM_LABELS[key] || key;

const isValidUrl = (value: string): boolean => {
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
};

const FA_ICON_SX = { width: 20, height: 20 } as const;

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
    onlyfans: <IconOnlyFans fontSize="small" />,
    instagram: <IconInstagram fontSize="small" />,
    twitter: <IconTwitter fontSize="small" />,
    tiktok: <IconTikTok fontSize="small" />,
    reddit: <IconReddit fontSize="small" />,
    fansly: (
        <SvgIcon fontSize="small" viewBox="0 0 24 24">
            <path
                d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.207 14.207a1 1 0 01-1.414 0L12 13.414l-2.793 2.793a1 1 0 01-1.414-1.414L10.586 12 7.793 9.207a1 1 0 011.414-1.414L12 10.586l2.793-2.793a1 1 0 011.414 1.414L13.414 12l2.793 2.793a1 1 0 010 1.414z"
                fill="#1fa7f1"
            />
        </SvgIcon>
    ),
    snapchat: <FontAwesomeIcon icon={faSnapchat} style={{ ...FA_ICON_SX, color: '#FFFC00' }} />,
    youtube: <FontAwesomeIcon icon={faYoutube} style={{ ...FA_ICON_SX, color: '#FF0000' }} />,
    twitch: <FontAwesomeIcon icon={faTwitch} style={{ ...FA_ICON_SX, color: '#9146FF' }} />,
    telegram: <FontAwesomeIcon icon={faTelegram} style={{ ...FA_ICON_SX, color: '#26A5E4' }} />,
    discord: <FontAwesomeIcon icon={faDiscord} style={{ ...FA_ICON_SX, color: '#5865F2' }} />,
    linktree: (
        <SvgIcon fontSize="small" viewBox="0 0 24 24">
            <path
                d="M7.953 15.066l-.038-2.04 4.038-3.546 4.068 3.546-.04 2.04-4.028-2.658zm4.044-7.996L7.2 11.268l-1.4-1.588L12 4.396l6.2 5.284-1.4 1.588zM11 19.5h2V14h-2z"
                fill="#43E660"
            />
        </SvgIcon>
    ),
    amazon: <FontAwesomeIcon icon={faAmazon} style={{ ...FA_ICON_SX, color: '#FF9900' }} />,
    cashapp: (
        <SvgIcon fontSize="small" viewBox="0 0 24 24">
            <path
                d="M23.59 3.47A5.1 5.1 0 0020.55.42C19.11-.04 15.71 0 12 0S4.89-.04 3.45.42A5.1 5.1 0 00.41 3.47C-.01 4.91 0 8.3 0 12s-.01 7.09.41 8.53a5.1 5.1 0 003.04 3.05c1.44.46 4.85.42 8.55.42s7.11.04 8.55-.42a5.1 5.1 0 003.04-3.05c.42-1.44.41-4.82.41-8.53s.01-7.09-.41-8.53zM17.3 16.13a3.82 3.82 0 01-2.7 1.7c-.67.1-1.37 0-2.03-.17l-.46-.13a.52.52 0 01-.36-.5v-1.52a.52.52 0 01.68-.49c.62.2 1.27.35 1.93.27.56-.07.8-.35.81-.69.02-.37-.21-.6-.63-.76-.55-.21-1.12-.37-1.65-.62A3.1 3.1 0 0111.1 11a3.04 3.04 0 01.66-3.24 3.6 3.6 0 012.3-1.28c.59-.09 1.2-.02 1.79.1l.34.08a.52.52 0 01.39.5v1.43a.52.52 0 01-.66.5 3.7 3.7 0 00-1.6-.2c-.49.06-.73.3-.75.61-.02.35.2.56.58.72.54.22 1.1.38 1.62.63A3.22 3.22 0 0117.9 13a3.13 3.13 0 01-.6 3.13z"
                fill="#00D632"
            />
        </SvgIcon>
    ),
};

const platformIcon = (key: string): React.ReactNode => PLATFORM_ICONS[key] || <LinkIcon fontSize="small" color="action" />;

const emptyPublicProfileForm: OnlyFansSextforcePpvMenuPublicProfile = {
    active: false,
    name: '',
    description: '',
    trackingLink: '',
    location: null,
};

const normalizeOnlyFansProfileText = (value?: string | null): string => {
    if (!value) {
        return '';
    }

    return value
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .trim();
};

const OnlyPromote = () => {
    const settingsContext = useContext(SettingsContext);
    const userContext = useContext(UserContext);
    const dialog = useDialog();

    const { data: subscriber, isLoading: isSubscriberLoading, setServiceRunningLoading, setServiceSettings } = useSubscriber();

    const [publicProfileForm, setPublicProfileForm] = useState<OnlyFansSextforcePpvMenuPublicProfile>(emptyPublicProfileForm);
    const [announceLiveStream, setAnnounceLiveStream] = useState(false);
    const [creatorBoostScore, setCreatorBoostScore] = useState(0);
    const [savedCreatorBoostScore, setSavedCreatorBoostScore] = useState(0);
    const { refetch: refetchSubscriberMe, isFetching: isSubscriberMeFetching } = useSubscriberMe();

    const {
        data: trackingMetrics = [],
        isLoading: trackingMetricsLoading,
        isError: trackingMetricsError,
        refetch: refetchTrackingMetrics,
    } = useCampaignsAndTrialsTrackingMetrics(Boolean(subscriber?.isLoggedIn));

    const { createCampaign, isCreateCampaignLoading } = useSextforceMetricsCampaignsCreate();
    const { createTrial, isCreateTrialLoading } = useSextforceMetricsTrialsCreate();

    const { linksQuery, createLink, isCreateLinkLoading, updateLink, isUpdateLinkLoading, deleteLink, isDeleteLinkLoading } =
        useOnlyPromoteLinks();

    const [linkEditorOpen, setLinkEditorOpen] = useState(false);
    const [editingLink, setEditingLink] = useState<OnlyPromoteLink | null>(null);
    const [linkForm, setLinkForm] = useState({ name: '', platform: 'other', url: '' });
    const [linkSearchQuery, setLinkSearchQuery] = useState('');
    const [linkPlatformFilter, setLinkPlatformFilter] = useState<string>('all');
    const [linkReorderOpen, setLinkReorderOpen] = useState(false);
    const [statsDays, setStatsDays] = useState<number>(30);

    const {
        referrersQuery,
        createReferrer,
        isCreateReferrerLoading,
        updateReferrer,
        isUpdateReferrerLoading,
        deleteReferrer,
        isDeleteReferrerLoading,
    } = useOnlyPromoteReferrers();

    const [referrerEditorOpen, setReferrerEditorOpen] = useState(false);
    const [editingReferrer, setEditingReferrer] = useState<OnlyPromoteReferrer | null>(null);
    const [referrerName, setReferrerName] = useState('');

    const [trackingActionsAnchorEl, setTrackingActionsAnchorEl] = useState<null | HTMLElement>(null);
    const [campaignCreateDialogOpen, setCampaignCreateDialogOpen] = useState(false);
    const [trialCreateDialogOpen, setTrialCreateDialogOpen] = useState(false);
    const [newCampaignName, setNewCampaignName] = useState('');
    const [newTrialName, setNewTrialName] = useState('');

    const trackingCreateBusy = isCreateCampaignLoading || isCreateTrialLoading;
    const serviceActive = subscriber?.sextforce?.ppvMenu?.active ?? false;

    const applyCreatedMetricToTracking = async (metric: OnlyFansMetrics | null | undefined) => {
        if (!metric || !subscriber?.username) {
            return;
        }
        await refetchTrackingMetrics();
        const url = buildMetricTrackingUrl({ type: metric.type, payload: metric.payload }, subscriber.username);
        setPublicProfileForm(prev => ({ ...prev, trackingLink: url }));
        enqueueSnackbar(metric.type === 'promoCampaign' ? 'Tracking link created and selected.' : 'Free trial link created and selected.', {
            variant: 'success',
            autoHideDuration: 2500,
        });
    };

    const selectedTrackingMetricId = useMemo(() => {
        if (!subscriber?.username || trackingMetrics.length === 0) {
            return '';
        }
        const id = findMetricIdForSavedTrackingLink(trackingMetrics, subscriber.username, publicProfileForm.trackingLink);
        return id ? String(id) : '';
    }, [subscriber?.username, trackingMetrics, publicProfileForm.trackingLink]);

    /**
     * True when the public-profile form has unsaved changes versus the persisted subscriber settings. Used to
     * highlight the Save button (`color="secondary"`) so the user can see at a glance that the form is dirty.
     * Normalization here mirrors `savePublicProfileSettings`: strings are trimmed, empties become `null`.
     */
    const hasPublicProfileChanges = useMemo(() => {
        const saved = subscriber?.sextforce?.ppvMenu?.publicProfile;
        const norm = (v: string | null | undefined) => (v ?? '').trim() || null;
        const savedAnnounce =
            typeof subscriber?.sextforce?.ppvMenu?.announceLiveStream === 'boolean'
                ? subscriber.sextforce.ppvMenu.announceLiveStream
                : false;

        return (
            (saved?.active ?? false) !== publicProfileForm.active ||
            norm(saved?.name) !== norm(publicProfileForm.name) ||
            norm(saved?.description) !== norm(publicProfileForm.description) ||
            norm(saved?.trackingLink) !== norm(publicProfileForm.trackingLink) ||
            norm(saved?.location) !== norm(publicProfileForm.location) ||
            savedAnnounce !== announceLiveStream
        );
    }, [subscriber, publicProfileForm, announceLiveStream]);

    const filteredLinks = useMemo(() => {
        const links = linksQuery.data ?? [];
        const q = linkSearchQuery.trim().toLowerCase();
        return links.filter(link => {
            if (linkPlatformFilter !== 'all' && link.platform !== linkPlatformFilter) return false;
            if (q && !link.name.toLowerCase().includes(q) && !link.url.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [linksQuery.data, linkSearchQuery, linkPlatformFilter]);

    useEffect(() => {
        if (!subscriber) {
            return;
        }
        const pp = subscriber.sextforce?.ppvMenu?.publicProfile;
        setPublicProfileForm({
            active: typeof pp?.active === 'boolean' ? pp.active : false,
            name: pp?.name || '',
            description: pp?.description || '',
            trackingLink: pp?.trackingLink || '',
            location: normalizeOnlyPromoteCountryCode(pp?.location),
        });
        setAnnounceLiveStream(
            typeof subscriber.sextforce?.ppvMenu?.announceLiveStream === 'boolean'
                ? subscriber.sextforce.ppvMenu.announceLiveStream
                : false,
        );
        const boost =
            typeof subscriber.sextforce?.ppvMenu?.creatorBoostScore === 'number'
                ? Math.min(100, Math.max(0, Math.round(subscriber.sextforce.ppvMenu.creatorBoostScore)))
                : 0;
        setCreatorBoostScore(boost);
        setSavedCreatorBoostScore(boost);
    }, [subscriber]);

    const creatorBoostScoreValid = useMemo(() => {
        if (!userContext.isAdmin) {
            return true;
        }
        const n = Number(creatorBoostScore);
        return Number.isInteger(n) && n >= 0 && n <= 100;
    }, [userContext.isAdmin, creatorBoostScore]);

    const hasCreatorBoostChanges = userContext.isAdmin && creatorBoostScore !== savedCreatorBoostScore;

    const publicProfileNameTrimmed = publicProfileForm.name.trim();
    const publicProfileNameMissingForPublic = publicProfileForm.active && !publicProfileNameTrimmed;

    const buildPpvMenuSettingsPayload = (options?: { includeCreatorBoost?: boolean }) => {
        const ppvMenu = subscriber?.sextforce?.ppvMenu;
        const publicProfile: OnlyFansSextforcePpvMenuPublicProfile = {
            active: publicProfileForm.active,
            name: publicProfileForm.name.trim(),
            description: publicProfileForm?.description?.trim() || null,
            trackingLink: publicProfileForm?.trackingLink?.trim() || null,
            location: normalizeOnlyPromoteCountryCode(publicProfileForm.location),
        };
        const payload: Record<string, unknown> = {
            active: typeof ppvMenu?.active === 'boolean' ? ppvMenu.active : serviceActive,
            expireWorkerNotifyTelegram:
                typeof ppvMenu?.expireWorkerNotifyTelegram === 'boolean' ? ppvMenu.expireWorkerNotifyTelegram : true,
            publicProfile,
            announceLiveStream,
        };
        if (options?.includeCreatorBoost && userContext.isAdmin) {
            payload.creatorBoostScore = Math.min(100, Math.max(0, Math.round(Number(creatorBoostScore))));
        }
        return payload;
    };

    const savePublicProfileSettings = async () => {
        if (publicProfileNameMissingForPublic) {
            enqueueSnackbar('Enter a public profile name before showing your profile on OnlyPromote.me.', {
                variant: 'warning',
                autoHideDuration: 3500,
            });
            return;
        }

        try {
            await setServiceSettings('sextforce.ppvMenu', buildPpvMenuSettingsPayload());
            enqueueSnackbar('OnlyPromote.me public profile updated.', { variant: 'success', autoHideDuration: 2500 });
        } catch {
            enqueueSnackbar('Failed to update public profile settings.', { variant: 'error', autoHideDuration: 3500 });
        }
    };

    const saveCreatorBoostScore = async () => {
        if (!creatorBoostScoreValid) {
            return;
        }
        try {
            await setServiceSettings('sextforce.ppvMenu', buildPpvMenuSettingsPayload({ includeCreatorBoost: true }));
            const normalized = Math.min(100, Math.max(0, Math.round(Number(creatorBoostScore))));
            setCreatorBoostScore(normalized);
            setSavedCreatorBoostScore(normalized);
            enqueueSnackbar('Creator directory boost updated.', { variant: 'success', autoHideDuration: 2500 });
        } catch {
            enqueueSnackbar('Failed to update creator boost.', { variant: 'error', autoHideDuration: 3500 });
        }
    };

    const copyPublicProfileFromOnlyFans = async () => {
        try {
            const { data } = await refetchSubscriberMe();
            const me = data?.me;
            if (!me) {
                enqueueSnackbar('Could not load your OnlyFans profile.', { variant: 'error', autoHideDuration: 3500 });
                return;
            }
            setPublicProfileForm(prev => ({
                ...prev,
                name: normalizeOnlyFansProfileText(me.name).slice(0, PUBLIC_PROFILE_NAME_MAX),
                description: normalizeOnlyFansProfileText(me.about).slice(0, PUBLIC_PROFILE_DESCRIPTION_MAX),
                location: normalizeOnlyPromoteCountryCode(me.location) ?? prev.location ?? null,
            }));
            enqueueSnackbar('Copied display name and bio from OnlyFans.', { variant: 'success', autoHideDuration: 2500 });
        } catch {
            enqueueSnackbar('Could not load your OnlyFans profile.', { variant: 'error', autoHideDuration: 3500 });
        }
    };

    const openLinkCreateDialog = () => {
        setEditingLink(null);
        setLinkForm({ name: '', platform: 'other', url: '' });
        setLinkEditorOpen(true);
    };

    const openLinkEditDialog = (link: OnlyPromoteLink) => {
        setEditingLink(link);
        setLinkForm({ name: link.name, platform: link.platform, url: link.url });
        setLinkEditorOpen(true);
    };

    const handleLinkSave = async () => {
        const trimmedUrl = linkForm.url.trim();
        if (!isValidUrl(trimmedUrl)) {
            enqueueSnackbar('Please enter a valid URL starting with http:// or https://', { variant: 'warning', autoHideDuration: 3500 });
            return;
        }
        const payload = { name: linkForm.name.trim(), platform: linkForm.platform, url: trimmedUrl };
        try {
            if (editingLink) {
                await updateLink({ linkId: editingLink.id, payload });
                enqueueSnackbar('Link updated.', { variant: 'success', autoHideDuration: 2500 });
            } else {
                await createLink(payload);
                enqueueSnackbar('Link created.', { variant: 'success', autoHideDuration: 2500 });
            }
            setLinkEditorOpen(false);
        } catch {
            enqueueSnackbar('Failed to save link.', { variant: 'error', autoHideDuration: 3500 });
        }
    };

    const handleLinkDelete = async (link: OnlyPromoteLink) => {
        await dialog
            .confirm({
                title: 'Delete Link?',
                message: `Remove "${link.name}" permanently?`,
                ok: { color: 'error', text: 'Delete', variant: 'contained' },
            })
            .then(async () => {
                try {
                    await deleteLink(link.id);
                    enqueueSnackbar('Link deleted.', { variant: 'success', autoHideDuration: 2500 });
                } catch {
                    enqueueSnackbar('Failed to delete link.', { variant: 'error', autoHideDuration: 3500 });
                }
            })
            .catch(() => null);
    };

    const openReferrerCreateDialog = () => {
        setEditingReferrer(null);
        setReferrerName('');
        setReferrerEditorOpen(true);
    };

    const openReferrerEditDialog = (ref: OnlyPromoteReferrer) => {
        setEditingReferrer(ref);
        setReferrerName(ref.name);
        setReferrerEditorOpen(true);
    };

    const handleReferrerSave = async () => {
        const trimmed = referrerName.trim();
        if (!trimmed) return;
        try {
            if (editingReferrer) {
                await updateReferrer({ referrerId: editingReferrer.id, name: trimmed });
                enqueueSnackbar('Referrer updated.', { variant: 'success', autoHideDuration: 2500 });
            } else {
                await createReferrer(trimmed);
                enqueueSnackbar('Referrer created.', { variant: 'success', autoHideDuration: 2500 });
            }
            setReferrerEditorOpen(false);
        } catch {
            enqueueSnackbar('Failed to save referrer.', { variant: 'error', autoHideDuration: 3500 });
        }
    };

    const handleReferrerDelete = async (ref: OnlyPromoteReferrer) => {
        await dialog
            .confirm({
                title: 'Delete Referrer?',
                message: `Remove "${ref.name}" permanently? Click data will be lost.`,
                ok: { color: 'error', text: 'Delete', variant: 'contained' },
            })
            .then(async () => {
                try {
                    await deleteReferrer(ref.id);
                    enqueueSnackbar('Referrer deleted.', { variant: 'success', autoHideDuration: 2500 });
                } catch {
                    enqueueSnackbar('Failed to delete referrer.', { variant: 'error', autoHideDuration: 3500 });
                }
            })
            .catch(() => null);
    };

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} spacing={2}>
                        <Typography variant="h5">
                            <BackNavigationButton url={`/subscribers/${subscriber?._id}/${settingsContext.services.inventory.homeUrl}`} />
                            OnlyPromote.me for {subscriber.username}
                        </Typography>
                    </Stack>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="OnlyPromote.me" />
                    </StyledCard>

                    <StyledCard sx={{ overflow: 'hidden' }}>
                        <Stack spacing={2}>
                            <Typography variant="body1" color="text.primary">
                                <MuiLink
                                    href="https://onlypromote.me"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ fontWeight: 700, textDecoration: 'none', color: 'primary.main' }}
                                >
                                    OnlyPromote.me
                                </MuiLink>{' '}
                                is a free, public directory of OnlyStruggles creators and their PPV menu offerings. It is indexed by search
                                engines and helps you get discovered by new fans browsing for content to purchase.
                            </Typography>
                            <Typography variant="body1" color="text.primary">
                                Your personal OnlyPromote.me page also works as a <strong>Linktree-style link page</strong> for all your
                                social media. Add your OnlyFans, Instagram, TikTok, Twitter, and any other accounts — links open in the
                                phone's own browser instead of inside Instagram or TikTok's in-app browser, so fans actually land on your
                                pages. Every click is counted and stats are displayed here.
                            </Typography>
                            <Typography variant="body1" color="text.primary">
                                From this page you can manage your public profile (display name, bio, and tracking link), add social and
                                promotional links, and set up referrer tags to track where your visitors come from.
                            </Typography>
                            <Typography variant="body1" color="text.primary">
                                To list individual menu items on OnlyPromote.me, mark them as <strong>public</strong> when editing each item
                                on the PPV Menu page.
                            </Typography>
                            <Alert severity="info">Changes can take up to 30 minutes to appear on OnlyPromote.me</Alert>
                        </Stack>
                    </StyledCard>

                    <StyledCard sx={{ bgcolor: 'grey.50' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ md: 'flex-start' }}>
                            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: '50%',
                                            bgcolor: theme => alpha(theme.palette.primary.main, 0.14),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <PublicOutlinedIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                    </Box>
                                    <Stack direction="column" spacing={0.5}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                            Your public profile
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            This is how fans find and visit your profile on OnlyPromote.me.
                                        </Typography>
                                    </Stack>
                                </Stack>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                                    <TextField
                                        fullWidth
                                        // Disable when the profile is not public
                                        disabled={!publicProfileForm.active}
                                        size="small"
                                        value={`onlypromote.me/${subscriber.username}`}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Copy profile link">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(
                                                                    `https://onlypromote.me/${subscriber.username}`,
                                                                );
                                                                enqueueSnackbar('Profile link copied!', {
                                                                    variant: 'success',
                                                                    autoHideDuration: 2000,
                                                                });
                                                            }}
                                                            aria-label="Copy profile link"
                                                        >
                                                            <ContentCopyIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: 'background.paper',
                                            },
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="medium"
                                        disabled={!publicProfileForm.active}
                                        component={MuiLink}
                                        href={`https://onlypromote.me/${subscriber.username}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="none"
                                        startIcon={<OpenInNewIcon />}
                                        sx={{
                                            minWidth: { sm: 160 },
                                        }}
                                    >
                                        Open profile
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Stats" />
                    </StyledCard>
                    <StyledCard sx={{ overflow: 'hidden' }}>
                        <OnlyPromoteStatsCard days={statsDays} onDaysChange={setStatsDays} />
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Profile Settings" />
                    </StyledCard>
                    <StyledCard>
                        <Stack spacing={2.5}>
                            <Typography variant="body2" color="text.secondary">
                                Control how your creator profile appears on{' '}
                                <MuiLink
                                    href="https://onlypromote.me"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ fontWeight: 600, textDecoration: 'none', color: 'primary.main' }}
                                >
                                    OnlyPromote.me
                                </MuiLink>{' '}
                                when you list public menu items.
                            </Typography>

                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ gap: 1 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                    <PublicOutlinedIcon
                                        sx={{
                                            fontSize: 28,
                                            color: 'primary.main',
                                            mt: 0.25,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                            Show profile on OnlyPromote.me
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            Allow your public profile and menu categories to appear in the directory. Requires a public
                                            profile name below.
                                        </Typography>
                                    </Box>
                                </Stack>
                                <LargeMaterialUISwitch
                                    checked={publicProfileForm.active}
                                    onChange={e => {
                                        const nextActive = e.target.checked;
                                        if (nextActive && !publicProfileNameTrimmed) {
                                            enqueueSnackbar('Enter a public profile name before enabling your OnlyPromote.me listing.', {
                                                variant: 'warning',
                                                autoHideDuration: 3500,
                                            });
                                            return;
                                        }
                                        setPublicProfileForm(prev => ({ ...prev, active: nextActive }));
                                    }}
                                    color="success"
                                />
                            </Stack>

                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ gap: 1 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                    <LiveTvOutlinedIcon
                                        sx={{
                                            fontSize: 28,
                                            color: 'primary.main',
                                            mt: 0.25,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                            Announce live streams on OnlyPromote.me
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            When you go live on OnlyFans, show your stream on OnlyPromote.me while it is active.
                                        </Typography>
                                    </Box>
                                </Stack>
                                <LargeMaterialUISwitch
                                    checked={announceLiveStream}
                                    onChange={e => setAnnounceLiveStream(e.target.checked)}
                                    color="success"
                                />
                            </Stack>

                            <TextField
                                fullWidth
                                variant="outlined"
                                label="Public profile name"
                                required={publicProfileForm.active}
                                value={publicProfileForm.name}
                                error={publicProfileNameMissingForPublic}
                                inputProps={{ maxLength: PUBLIC_PROFILE_NAME_MAX }}
                                onChange={e => {
                                    const name = e.target.value.slice(0, PUBLIC_PROFILE_NAME_MAX);
                                    setPublicProfileForm(prev => {
                                        const next = { ...prev, name };
                                        if (prev.active && !name.trim()) {
                                            return { ...next, active: false };
                                        }
                                        return next;
                                    });
                                }}
                                helperText={
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="baseline"
                                        component="span"
                                        sx={{ width: '100%', typography: 'caption' }}
                                    >
                                        <span>
                                            {publicProfileNameMissingForPublic
                                                ? 'Required to show your profile on OnlyPromote.me.'
                                                : 'Displayed as your profile title on OnlyPromote.me.'}
                                        </span>
                                        <Box component="span" sx={{ color: 'text.secondary', flexShrink: 0, ml: 1 }}>
                                            {publicProfileForm.name.length} / {PUBLIC_PROFILE_NAME_MAX}
                                        </Box>
                                    </Stack>
                                }
                                FormHelperTextProps={{ component: 'div' }}
                            />

                            <FormControl fullWidth variant="outlined">
                                <InputLabel id="onlypromote-country-label">Country (optional)</InputLabel>
                                <Select
                                    labelId="onlypromote-country-label"
                                    label="Country (optional)"
                                    value={publicProfileForm.location ?? ''}
                                    onChange={e => {
                                        const v = e.target.value;
                                        setPublicProfileForm(prev => ({
                                            ...prev,
                                            location: v ? normalizeOnlyPromoteCountryCode(v) : null,
                                        }));
                                    }}
                                >
                                    <DropdownMenuItem value="">
                                        <em>None</em>
                                    </DropdownMenuItem>
                                    {ONLY_PROMOTE_COUNTRY_OPTIONS.map(country => (
                                        <DropdownMenuItem key={country.code} value={country.code}>
                                            {country.name}
                                        </DropdownMenuItem>
                                    ))}
                                </Select>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', px: 0.25 }}>
                                    {publicProfileForm.location
                                        ? `Shown on OnlyPromote.me as ${onlyPromoteCountryName(publicProfileForm.location)} (${
                                              publicProfileForm.location
                                          }).`
                                        : 'Optional country for your public directory listing on OnlyPromote.me.'}
                                </Typography>
                            </FormControl>

                            <TextField
                                fullWidth
                                variant="outlined"
                                multiline
                                minRows={4}
                                label="Public profile description"
                                value={publicProfileForm.description}
                                inputProps={{ maxLength: PUBLIC_PROFILE_DESCRIPTION_MAX }}
                                onChange={e =>
                                    setPublicProfileForm(prev => ({
                                        ...prev,
                                        description: e.target.value.slice(0, PUBLIC_PROFILE_DESCRIPTION_MAX),
                                    }))
                                }
                                helperText={
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        component="span"
                                        sx={{ width: '100%', typography: 'caption' }}
                                    >
                                        <span>Optional bio for your profile page.</span>
                                        <Box component="span" sx={{ color: 'text.secondary', flexShrink: 0, ml: 1 }}>
                                            {(publicProfileForm.description || '').length} / {PUBLIC_PROFILE_DESCRIPTION_MAX}
                                        </Box>
                                    </Stack>
                                }
                                FormHelperTextProps={{ component: 'div' }}
                            />

                            <Stack direction="row" justifyContent="flex-start">
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={isSubscriberMeFetching ? <CircularProgress size={16} /> : <ContentCopyIcon />}
                                    disabled={!subscriber.isLoggedIn || isSubscriberMeFetching}
                                    onClick={copyPublicProfileFromOnlyFans}
                                >
                                    Copy from OnlyFans
                                </Button>
                            </Stack>

                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        disabled={!subscriber.isLoggedIn || trackingMetricsLoading}
                                        sx={{ flex: 1, minWidth: 0 }}
                                    >
                                        <InputLabel id="onlypromote-tracking-metric-label">Tracking link (optional)</InputLabel>
                                        <Select
                                            labelId="onlypromote-tracking-metric-label"
                                            label="Tracking link (optional)"
                                            value={selectedTrackingMetricId}
                                            onChange={e => {
                                                const v = e.target.value as string;
                                                if (!v) {
                                                    setPublicProfileForm(prev => ({ ...prev, trackingLink: '' }));
                                                    return;
                                                }
                                                const m = trackingMetrics.find(row => String(row._id) === v);
                                                if (m && subscriber?.username) {
                                                    setPublicProfileForm(prev => ({
                                                        ...prev,
                                                        trackingLink: buildMetricTrackingUrl(m, subscriber.username),
                                                    }));
                                                }
                                            }}
                                        >
                                            <MuiMenuItem value="">
                                                <em>None</em>
                                            </MuiMenuItem>
                                            {trackingMetrics.map(m => (
                                                <MuiMenuItem key={String(m._id)} value={String(m._id)}>
                                                    {m.type === 'promoCampaign' ? 'Campaign' : 'Trial'}:{' '}
                                                    {m.payload?.name?.trim() || `#${m.foreignId}`}
                                                </MuiMenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Tooltip title="Create tracking or trial link">
                                        <span>
                                            <IconButton
                                                aria-label="Create tracking or trial link"
                                                edge="end"
                                                size="small"
                                                disabled={!subscriber.isLoggedIn || trackingMetricsLoading || trackingCreateBusy}
                                                onClick={e => setTrackingActionsAnchorEl(e.currentTarget)}
                                                sx={{ flexShrink: 0, alignSelf: 'center' }}
                                            >
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Menu
                                        anchorEl={trackingActionsAnchorEl}
                                        open={Boolean(trackingActionsAnchorEl)}
                                        onClose={() => setTrackingActionsAnchorEl(null)}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    >
                                        <DropdownMenuItem
                                            onClick={() => {
                                                setTrackingActionsAnchorEl(null);
                                                setNewCampaignName('');
                                                setCampaignCreateDialogOpen(true);
                                            }}
                                        >
                                            New tracking link…
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={() => {
                                                setTrackingActionsAnchorEl(null);
                                                setNewTrialName('');
                                                setTrialCreateDialogOpen(true);
                                            }}
                                        >
                                            New free trial link…
                                        </DropdownMenuItem>
                                    </Menu>
                                </Stack>
                                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75, px: 0.25 }}>
                                    {trackingMetricsError
                                        ? 'Could not load campaigns and trials.'
                                        : trackingMetricsLoading
                                        ? 'Loading campaigns and trials…'
                                        : publicProfileForm.trackingLink && !selectedTrackingMetricId
                                        ? 'Saved URL does not match an active campaign or trial. Choose an option to replace it, or clear with None.'
                                        : 'Optional OnlyFans campaign or trial link for tracking visits from your OnlyPromote.me profile.'}
                                </Typography>
                            </Box>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap" sx={{ pt: 0.5 }}>
                                <Button
                                    variant="contained"
                                    color={hasPublicProfileChanges ? 'secondary' : 'primary'}
                                    startIcon={setServiceRunningLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                    disabled={setServiceRunningLoading || isSubscriberLoading || publicProfileNameMissingForPublic}
                                    onClick={savePublicProfileSettings}
                                >
                                    Save public profile
                                </Button>
                            </Stack>
                        </Stack>
                    </StyledCard>

                    {userContext.isAdmin && (
                        <>
                            <StyledCard noCard noBottomMargin>
                                <StyledCardTitleBar title="Directory boost (admin)" />
                            </StyledCard>
                            <StyledCard>
                                <Stack spacing={2}>
                                    <Alert severity="info">
                                        <strong>Admin only.</strong> Permanent boost for this creator on OnlyPromote.me (0-100). Separate
                                        from per-menu-item boost scores. Included in the public API for ranking; hidden from non-admin
                                        subscribers in the dashboard.
                                    </Alert>
                                    <TextField
                                        label="Creator boost score"
                                        type="number"
                                        value={creatorBoostScore}
                                        onChange={e => {
                                            const raw = e.target.value;
                                            if (raw === '') {
                                                setCreatorBoostScore(0);
                                                return;
                                            }
                                            const n = Number.parseInt(raw, 10);
                                            if (Number.isNaN(n)) {
                                                return;
                                            }
                                            setCreatorBoostScore(Math.min(100, Math.max(0, n)));
                                        }}
                                        inputProps={{ min: 0, max: 100, step: 1 }}
                                        disabled={setServiceRunningLoading || isSubscriberLoading}
                                        error={!creatorBoostScoreValid}
                                        helperText={
                                            creatorBoostScoreValid
                                                ? 'Higher values can prioritize this creator in directory listings on onlypromote.me.'
                                                : 'Enter a whole number from 0 to 100.'
                                        }
                                        fullWidth
                                    />
                                    <Button
                                        variant="contained"
                                        color={hasCreatorBoostChanges ? 'secondary' : 'primary'}
                                        startIcon={setServiceRunningLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                        disabled={
                                            setServiceRunningLoading ||
                                            isSubscriberLoading ||
                                            !creatorBoostScoreValid ||
                                            !hasCreatorBoostChanges
                                        }
                                        onClick={saveCreatorBoostScore}
                                        sx={{ alignSelf: 'flex-start' }}
                                    >
                                        Save creator boost
                                    </Button>
                                </Stack>
                            </StyledCard>
                        </>
                    )}

                    {/* ── OnlyPromote Links ─────────────────────── */}
                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar
                            title="My Social Links"
                            isLoading={linksQuery.isLoading || isUpdateLinkLoading || isDeleteLinkLoading || isCreateLinkLoading}
                        />
                    </StyledCard>
                    <StyledCard>
                        <Stack spacing={2.5}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Links
                                    </Typography>
                                    <Chip label={`${linksQuery.data?.length ?? 0} links`} size="small" color="primary" variant="outlined" />
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ReorderIcon />}
                                        onClick={() => setLinkReorderOpen(true)}
                                        disabled={(linksQuery.data?.length ?? 0) < 2}
                                    >
                                        Reorder
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={openLinkCreateDialog}
                                        disabled={isCreateLinkLoading}
                                    >
                                        Add link
                                    </Button>
                                </Stack>
                            </Stack>

                            <Typography variant="body2" color="text.secondary">
                                Add your OnlyFans, social media, and any other links. Your OnlyPromote.me page works like Linktree — links
                                open in the phone's native browser (not inside Instagram or TikTok). Clicks are tracked automatically.
                            </Typography>

                            {(linksQuery.data?.length ?? 0) > 0 && (
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                    <TextField
                                        size="small"
                                        placeholder="Search links..."
                                        value={linkSearchQuery}
                                        onChange={e => setLinkSearchQuery(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ flex: 1, minWidth: 0 }}
                                    />
                                    <FormControl size="small" sx={{ minWidth: 160 }}>
                                        <InputLabel>Platform</InputLabel>
                                        <Select
                                            label="Platform"
                                            value={linkPlatformFilter}
                                            onChange={e => setLinkPlatformFilter(e.target.value)}
                                        >
                                            <MuiMenuItem value="all">All platforms</MuiMenuItem>
                                            {LINK_PLATFORMS.map(p => (
                                                <MuiMenuItem key={p} value={p}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        {platformIcon(p)}
                                                        <span>{platformLabel(p)}</span>
                                                    </Stack>
                                                </MuiMenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            )}

                            {linksQuery.isLoading ? (
                                <Grid container spacing={2}>
                                    {[0, 1, 2].map(i => (
                                        <Grid item xs={12} sm={6} md={4} key={i}>
                                            <Skeleton variant="rectangular" height={220} />
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (linksQuery.data?.length ?? 0) === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No links yet. Add your first link above.
                                </Typography>
                            ) : filteredLinks.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No links match your search or filter.
                                </Typography>
                            ) : (
                                <Box>
                                    <Grid container spacing={2}>
                                        {filteredLinks.map(link => {
                                            const publicUrl = `onlypromote.me/${subscriber?.username}/${link.platform}`;
                                            return (
                                                <Grid item xs={12} key={link.id}>
                                                    <Card
                                                        variant="outlined"
                                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                                    >
                                                        <CardContent sx={{ flex: 1, pb: 0 }}>
                                                            <Stack spacing={1.5}>
                                                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                                                    <Box
                                                                        sx={{
                                                                            width: 44,
                                                                            height: 44,
                                                                            borderRadius: '50%',
                                                                            bgcolor: 'action.hover',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        {platformIcon(link.platform)}
                                                                    </Box>
                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                                                                            {platformLabel(link.platform)}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary" noWrap>
                                                                            {link.name}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Chip
                                                                        label={link.active ? 'Active' : 'Inactive'}
                                                                        size="small"
                                                                        color={link.active ? 'success' : 'default'}
                                                                        onClick={async () => {
                                                                            try {
                                                                                await updateLink({
                                                                                    linkId: link.id,
                                                                                    payload: { active: !link.active },
                                                                                });
                                                                                enqueueSnackbar(
                                                                                    link.active ? 'Link deactivated.' : 'Link activated.',
                                                                                    { variant: 'success', autoHideDuration: 2000 },
                                                                                );
                                                                            } catch {
                                                                                enqueueSnackbar('Failed to update link.', {
                                                                                    variant: 'error',
                                                                                    autoHideDuration: 3500,
                                                                                });
                                                                            }
                                                                        }}
                                                                        disabled={isUpdateLinkLoading}
                                                                        sx={{ cursor: 'pointer' }}
                                                                    />
                                                                </Stack>

                                                                <Box
                                                                    sx={{
                                                                        bgcolor: 'action.hover',
                                                                        px: 1.5,
                                                                        py: 1,
                                                                    }}
                                                                >
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        justifyContent="space-between"
                                                                    >
                                                                        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                                                                            {link.url.replace(/(^\w+:|^)\/\//, '')}
                                                                        </Typography>
                                                                        <Tooltip title="Copy public link">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => {
                                                                                    navigator.clipboard.writeText(link.url);
                                                                                    enqueueSnackbar('Public link copied!', {
                                                                                        variant: 'success',
                                                                                        autoHideDuration: 2000,
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Stack>
                                                                </Box>

                                                                <Stack
                                                                    direction="row"
                                                                    spacing={2}
                                                                    alignItems="center"
                                                                    justifyContent={'space-between'}
                                                                >
                                                                    <Stack direction="row" spacing={3}>
                                                                        <Stack
                                                                            direction="column"
                                                                            sx={{ flexShrink: 0, textAlign: 'flex-start' }}
                                                                        >
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Clicks
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                {link.clickCount.toLocaleString()}
                                                                            </Typography>
                                                                        </Stack>
                                                                        <Stack
                                                                            direction="column"
                                                                            sx={{ flexShrink: 0, textAlign: 'flex-start' }}
                                                                        >
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Created
                                                                            </Typography>
                                                                            <Typography variant="body2">
                                                                                {new Date(link.createdAt).toLocaleDateString()}
                                                                            </Typography>
                                                                        </Stack>
                                                                    </Stack>
                                                                    <Stack direction="row" spacing={1}>
                                                                        <IconButton
                                                                            size="small"
                                                                            color="primary"
                                                                            onClick={() => openLinkEditDialog(link)}
                                                                            disabled={isUpdateLinkLoading || isDeleteLinkLoading}
                                                                        >
                                                                            <EditIcon />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            size="small"
                                                                            color="info"
                                                                            component="a"
                                                                            href={link.url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                        >
                                                                            <OpenInNewIcon />
                                                                        </IconButton>
                                                                    </Stack>
                                                                </Stack>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            )}
                        </Stack>
                    </StyledCard>

                    {/* ── OnlyPromote Referrers ──────────────────── */}
                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar
                            title={`Referrers (${referrersQuery.data?.length ?? 0})`}
                            isLoading={
                                referrersQuery.isLoading || isUpdateReferrerLoading || isDeleteReferrerLoading || isCreateReferrerLoading
                            }
                        />
                    </StyledCard>
                    <StyledCard>
                        <Stack spacing={2.5}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Referrers
                                    </Typography>
                                    <Chip
                                        label={`${referrersQuery.data?.length ?? 0} referrers`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                </Stack>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={openReferrerCreateDialog}
                                    disabled={isCreateReferrerLoading}
                                >
                                    Add referrer
                                </Button>
                            </Stack>

                            <Typography variant="body2" color="text.secondary">
                                Track which traffic sources are bringing fans to your OnlyPromote.me page. Create a referrer for each source
                                you use (e.g. Instagram bio, Twitter post, Reddit, newsletter) and use the matching referrer link when
                                sharing your page. Clicks per referrer are counted automatically and stats are shown here.
                            </Typography>

                            {referrersQuery.isLoading ? (
                                <Grid container spacing={2}>
                                    {[0, 1, 2].map(i => (
                                        <Grid item xs={12} sm={6} md={4} key={i}>
                                            <Skeleton variant="rectangular" height={180} />
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (referrersQuery.data?.length ?? 0) === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No referrers yet. Add your first referrer above.
                                </Typography>
                            ) : (
                                <Box>
                                    <Grid container spacing={2}>
                                        {(referrersQuery.data ?? []).map(ref => {
                                            const referrerUrl = `https://onlypromote.me/${subscriber?.username}?t=${ref.id}`;
                                            return (
                                                <Grid item xs={12} key={ref.id}>
                                                    <Card
                                                        variant="outlined"
                                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                                    >
                                                        <CardContent sx={{ flex: 1, pb: 0 }}>
                                                            <Stack spacing={1.5}>
                                                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                                                                            {ref.name}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Chip label={`ID: ${ref.id}`} size="small" variant="outlined" />
                                                                </Stack>

                                                                <Box
                                                                    sx={{
                                                                        bgcolor: 'action.hover',
                                                                        px: 1.5,
                                                                        py: 1,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ display: 'block', mb: 0.25 }}
                                                                    >
                                                                        Referrer link
                                                                    </Typography>
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        justifyContent="space-between"
                                                                    >
                                                                        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                                                                            {referrerUrl.replace('https://', '')}
                                                                        </Typography>
                                                                        <Tooltip title="Copy referrer link">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => {
                                                                                    navigator.clipboard.writeText(referrerUrl);
                                                                                    enqueueSnackbar('Referrer link copied!', {
                                                                                        variant: 'success',
                                                                                        autoHideDuration: 2000,
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Stack>
                                                                </Box>

                                                                <Stack
                                                                    direction="row"
                                                                    spacing={2}
                                                                    alignItems="center"
                                                                    justifyContent="space-between"
                                                                >
                                                                    <Stack direction="row" spacing={3} alignItems="center">
                                                                        <Stack
                                                                            direction="column"
                                                                            sx={{ flexShrink: 0, textAlign: 'flex-start' }}
                                                                        >
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Clicks
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                {ref.clickCount.toLocaleString()}
                                                                            </Typography>
                                                                        </Stack>
                                                                        <Stack
                                                                            direction="column"
                                                                            sx={{ flexShrink: 0, textAlign: 'flex-start' }}
                                                                        >
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Created
                                                                            </Typography>
                                                                            <Typography variant="body2">
                                                                                {new Date(ref.createdAt).toLocaleDateString()}
                                                                            </Typography>
                                                                        </Stack>
                                                                    </Stack>
                                                                    <Box>
                                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                                            <IconButton
                                                                                size="small"
                                                                                color="primary"
                                                                                onClick={() => openReferrerEditDialog(ref)}
                                                                                disabled={
                                                                                    isUpdateReferrerLoading || isDeleteReferrerLoading
                                                                                }
                                                                            >
                                                                                <EditIcon />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => handleReferrerDelete(ref)}
                                                                                disabled={isDeleteReferrerLoading}
                                                                            >
                                                                                <DeleteIcon />
                                                                            </IconButton>
                                                                        </Stack>
                                                                    </Box>
                                                                </Stack>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            )}
                        </Stack>
                    </StyledCard>

                    {/* ── Dialogs ──────────────────────────────────── */}
                    <Dialog open={campaignCreateDialogOpen} onClose={() => !isCreateCampaignLoading && setCampaignCreateDialogOpen(false)}>
                        <DialogTitle>New tracking link</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Name"
                                fullWidth
                                value={newCampaignName}
                                disabled={isCreateCampaignLoading}
                                onChange={e => setNewCampaignName(e.target.value)}
                                helperText="Creates a campaign link on OnlyFans and selects it for your public profile."
                                sx={{ mt: 0.5 }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setCampaignCreateDialogOpen(false)} disabled={isCreateCampaignLoading}>
                                Cancel
                            </Button>
                            <LoadingButton
                                variant="contained"
                                loading={isCreateCampaignLoading}
                                disabled={newCampaignName.trim().length === 0}
                                onClick={async () => {
                                    const created = await createCampaign(newCampaignName.trim());
                                    if (created) {
                                        setCampaignCreateDialogOpen(false);
                                        setNewCampaignName('');
                                        await applyCreatedMetricToTracking(created);
                                        refetchTrackingMetrics();
                                    }
                                }}
                            >
                                Create
                            </LoadingButton>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={linkEditorOpen}
                        onClose={() => !(isCreateLinkLoading || isUpdateLinkLoading) && setLinkEditorOpen(false)}
                        fullWidth
                        maxWidth="sm"
                    >
                        <StyledCardTitleBar title={editingLink ? 'Edit link' : 'New link'} />
                        <DialogContent>
                            <Stack spacing={2} sx={{ mt: 0.5 }}>
                                <TextField
                                    autoFocus
                                    label="Account / @ Name"
                                    fullWidth
                                    value={linkForm.name}
                                    onChange={e => setLinkForm(prev => ({ ...prev, name: e.target.value }))}
                                    inputProps={{ maxLength: 200 }}
                                    disabled={isCreateLinkLoading || isUpdateLinkLoading}
                                    placeholder={subscriber?.username || ''}
                                />
                                <FormControl fullWidth>
                                    <InputLabel>Platform</InputLabel>
                                    <Select
                                        label="Platform"
                                        value={linkForm.platform}
                                        onChange={e => setLinkForm(prev => ({ ...prev, platform: e.target.value }))}
                                        disabled={isCreateLinkLoading || isUpdateLinkLoading}
                                    >
                                        {LINK_PLATFORMS.map(p => (
                                            <MuiMenuItem key={p} value={p}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {platformIcon(p)}
                                                    <span>{platformLabel(p)}</span>
                                                </Stack>
                                            </MuiMenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Link to profile or content"
                                    fullWidth
                                    value={linkForm.url}
                                    onChange={e => setLinkForm(prev => ({ ...prev, url: e.target.value }))}
                                    inputProps={{ maxLength: 2000 }}
                                    disabled={isCreateLinkLoading || isUpdateLinkLoading}
                                    placeholder="https://"
                                />
                            </Stack>
                        </DialogContent>
                        <StyledDialogActions>
                            <Button onClick={() => setLinkEditorOpen(false)} disabled={isCreateLinkLoading || isUpdateLinkLoading}>
                                Cancel
                            </Button>
                            <LoadingButton
                                variant="contained"
                                loading={isCreateLinkLoading || isUpdateLinkLoading}
                                disabled={!linkForm.name.trim() || !isValidUrl(linkForm.url.trim())}
                                onClick={handleLinkSave}
                            >
                                {editingLink ? 'Save' : 'Create'}
                            </LoadingButton>
                        </StyledDialogActions>
                    </Dialog>

                    <Dialog
                        open={referrerEditorOpen}
                        onClose={() => !(isCreateReferrerLoading || isUpdateReferrerLoading) && setReferrerEditorOpen(false)}
                        fullWidth
                        maxWidth="xs"
                    >
                        <StyledCardTitleBar title={editingReferrer ? 'Edit referrer' : 'New referrer'} />
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Referrer name"
                                fullWidth
                                value={referrerName}
                                onChange={e => setReferrerName(e.target.value)}
                                inputProps={{ maxLength: 200 }}
                                disabled={isCreateReferrerLoading || isUpdateReferrerLoading}
                                helperText="A label to identify the traffic source (e.g. Reddit, Twitter bio, newsletter)."
                                sx={{ mt: 0.5 }}
                            />
                        </DialogContent>
                        <StyledDialogActions>
                            <Button
                                onClick={() => setReferrerEditorOpen(false)}
                                disabled={isCreateReferrerLoading || isUpdateReferrerLoading}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                variant="contained"
                                loading={isCreateReferrerLoading || isUpdateReferrerLoading}
                                disabled={!referrerName.trim()}
                                onClick={handleReferrerSave}
                            >
                                {editingReferrer ? 'Save' : 'Create'}
                            </LoadingButton>
                        </StyledDialogActions>
                    </Dialog>

                    <Dialog open={trialCreateDialogOpen} onClose={() => !isCreateTrialLoading && setTrialCreateDialogOpen(false)}>
                        <StyledCardTitleBar title="New free trial link" />
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Name"
                                fullWidth
                                value={newTrialName}
                                disabled={isCreateTrialLoading}
                                onChange={e => setNewTrialName(e.target.value)}
                                helperText="Uses default trial settings (no claim limit, no offer expiry, 7-day trial). Edit later under Sextforce metrics if needed."
                                sx={{ mt: 0.5 }}
                            />
                        </DialogContent>
                        <StyledDialogActions>
                            <Button onClick={() => setTrialCreateDialogOpen(false)} disabled={isCreateTrialLoading}>
                                Cancel
                            </Button>
                            <LoadingButton
                                variant="contained"
                                loading={isCreateTrialLoading}
                                disabled={newTrialName.trim().length === 0}
                                onClick={async () => {
                                    const created = await createTrial(newTrialName.trim(), 0, 0, 7);
                                    if (created) {
                                        setTrialCreateDialogOpen(false);
                                        setNewTrialName('');
                                        await applyCreatedMetricToTracking(created);
                                    }
                                }}
                            >
                                Create
                            </LoadingButton>
                        </StyledDialogActions>
                    </Dialog>

                    <LinkReorderModal
                        open={linkReorderOpen}
                        onClose={() => setLinkReorderOpen(false)}
                        links={linksQuery.data ?? []}
                        updateLink={updateLink}
                        onSaved={async () => {
                            await linksQuery.refetch();
                        }}
                    />
                </>
            )}
        </PageContainer>
    );
};

export default OnlyPromote;
